import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Feature, FeatureDepartment } from '../../types/feature';
import { Role } from '../../types/role';
import { featureService } from '../../services/featureService';
import { RoleService } from '../../services/roleService';
import { useAuth } from '../../context/AuthContext';
import { FeatureFormDrawer } from './form/FeatureFormDrawer';
import PageBreadcrumb from '../../shared/PageBreadCrumb';
import { SOVEREIGN } from '@/features/epc/utils/epcProjectVisual';

import FeatureBUTabsView from './components/FeatureBUTabsView';
import {
  ALL_BUS,
  BU,
  buildFeatureRoleMatrix,
  detectOrphanPermissions,
} from './utils/featureRoleMatrix';

function parseBuParam(raw: string | null): BU {
  const v = (raw ?? '').toUpperCase();
  return (ALL_BUS as string[]).includes(v) ? (v as BU) : 'OMS';
}

/** Build a concise toast message from a backend 422 validation error response. */
function formatValidationErrors(err: any, fallback: string): string {
  const errors = err?.response?.data?.errors;
  if (err?.response?.status === 422 && Array.isArray(errors) && errors.length > 0) {
    const summarized = errors
      .slice(0, 2)
      .map((e: { module?: string; action?: string; message?: string }) => {
        const head = e.module && e.action ? `${e.module}.${e.action}` : e.module ?? '';
        return head ? `${head}: ${e.message ?? ''}` : e.message ?? '';
      })
      .filter(Boolean)
      .join(' • ');
    if (summarized) {
      return errors.length > 2
        ? `${summarized} (+${errors.length - 2} more)`
        : summarized;
    }
  }
  return err?.response?.data?.message ?? fallback;
}

const FeaturesMenuPage: React.FC = () => {
  const [features, setFeatures] = useState<Feature[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [createDefaultDeptOverride, setCreateDefaultDeptOverride] =
    useState<FeatureDepartment | null>(null);
  const [editingFeature, setEditingFeature] = useState<Feature | null>(null);
  const [orphanBannerOpen, setOrphanBannerOpen] = useState(false);

  // Backend gates feature CRUD and role permission updates on `roleManagement.*`.
  const { hasPermission } = useAuth();
  const editable = hasPermission('roleManagement', 'update');

  const [searchParams] = useSearchParams();
  const activeBU = parseBuParam(searchParams.get('bu'));

  const loadFeatures = useCallback(async () => {
    try {
      setLoading(true);
      // Use the un-paginated all-for-business-unit endpoint with no BU filter
      // to fetch every feature in one go (the paginated /features default
      // limit would silently drop everything after the first page).
      const all = await featureService.getAllFeaturesForDept(undefined);
      setFeatures(all.filter(f => f.isActive !== false));
    } catch {
      toast.error('Failed to load features');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRoles = useCallback(async () => {
    try {
      setRolesLoading(true);
      const list = await RoleService.getAllRolesForDDL();
      setRoles(list);
    } catch {
      toast.error('Failed to load roles');
    } finally {
      setRolesLoading(false);
    }
  }, []);

  useEffect(() => {
    Promise.all([loadFeatures(), loadRoles()]);
  }, [loadFeatures, loadRoles]);

  const matrix = useMemo(
    () => buildFeatureRoleMatrix(features, roles),
    [features, roles],
  );

  const orphans = useMemo(
    () => detectOrphanPermissions(features, roles),
    [features, roles],
  );

  const existingKeys = useMemo(() => new Set(features.map(f => f.key)), [features]);

  // ── Feature actions ───────────────────────────────────────────────────────
  const handleToggleStatus = async (key: string) => {
    try {
      await featureService.toggleFeatureStatus(key);
      await loadFeatures();
    } catch (err: any) {
      toast.error(formatValidationErrors(err, 'Failed to update status'));
    }
  };

  const handleDelete = async (key: string) => {
    if (!window.confirm(`Delete feature "${key}"? This cannot be undone.`)) return;
    try {
      await featureService.deleteFeature(key);
      toast.success('Feature deleted');
      await loadFeatures();
    } catch (err: any) {
      toast.error(formatValidationErrors(err, 'Failed to delete feature'));
    }
  };

  const handleMakeCommon = async (key: string, currentDept: FeatureDepartment) => {
    const newDept: FeatureDepartment = currentDept === 'COMMON' ? 'OMS' : 'COMMON';
    const msg =
      currentDept === 'COMMON'
        ? `Move "${key}" from COMMON to OMS?`
        : `Make "${key}" COMMON (shared across all business units)?`;
    if (!window.confirm(msg)) return;
    try {
      await featureService.bulkUpdateDepartment([key], newDept);
      toast.success(`Feature moved to ${newDept}`);
      await loadFeatures();
    } catch (err: any) {
      toast.error(formatValidationErrors(err, 'Failed to update business unit'));
    }
  };

  const handleCreateSuccess = async () => {
    setShowCreate(false);
    await loadFeatures();
  };

  const handleEditSaved = async () => {
    setEditingFeature(null);
    await loadFeatures();
  };

  const handleMoveSubMenu = async (featureKey: string, newParentKey: string) => {
    const moving = features.find(f => f.key === featureKey);
    const target = features.find(f => f.key === newParentKey);
    if (!moving || !target) {
      toast.error('Could not find the feature or target parent');
      return;
    }
    if (moving.parentKey === newParentKey) return;

    // Optimistic local update so the row jumps to the new section instantly.
    setFeatures(prev =>
      prev.map(f =>
        f.key === featureKey ? { ...f, parentKey: newParentKey } : f,
      ),
    );

    try {
      await featureService.updateFeatureByKey(featureKey, {
        parentKey: newParentKey,
      });
      toast.success(`Moved "${moving.name}" under "${target.name}"`);
      await loadFeatures();
    } catch (err: any) {
      // Roll back optimistic update.
      setFeatures(prev =>
        prev.map(f =>
          f.key === featureKey ? { ...f, parentKey: moving.parentKey } : f,
        ),
      );
      toast.error(formatValidationErrors(err, 'Failed to move sub-menu'));
    }
  };

  return (
    <div className="min-h-screen" style={{ background: SOVEREIGN.paper }}>
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-6">
        <PageBreadcrumb
          pageTitle="Features"
          breadcrumbs={[
            { name: 'Home', path: '/' },
            { name: 'Admin', path: '/admin' },
            { name: 'Features' },
          ]}
        />

        {/* Header */}
        <div className="flex flex-wrap items-center justify-end gap-2 mb-4">
          {editable && (
            <button
              onClick={() => {
                setCreateDefaultDeptOverride(activeBU as FeatureDepartment);
                setShowCreate(true);
              }}
              className="flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg text-white transition-colors"
              style={{
                background: SOVEREIGN.btn,
                border: `1px solid ${SOVEREIGN.btnBorder}`,
                boxShadow: SOVEREIGN.btnShadow,
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = SOVEREIGN.btnHover; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = SOVEREIGN.btn; }}
            >
              + Add Feature
              <span
                className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                style={{ background: 'rgba(255,255,255,0.18)' }}
              >
                {activeBU}
              </span>
            </button>
          )}
        </div>

        {/* Orphan permission banner -- champagne-gold at-risk tone */}
        {orphans.length > 0 && (
          <div
            className="mb-4 rounded-xl px-4 py-3"
            style={{
              border: '1px solid rgba(201,169,97,0.38)',
              background: 'rgba(201,169,97,0.07)',
            }}
          >
            <div className="flex items-start gap-3">
              <span style={{ color: SOVEREIGN.champagne }} className="text-lg" aria-hidden>
                &#9888;
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-sm font-semibold" style={{ color: '#8A5A08' }}>
                    {orphans.length} role permission
                    {orphans.length === 1 ? '' : 's'} reference modules with no
                    matching feature
                  </div>
                  <button
                    onClick={() => setOrphanBannerOpen(v => !v)}
                    className="text-xs font-semibold"
                    style={{ color: SOVEREIGN.champagne }}
                  >
                    {orphanBannerOpen ? 'Hide details' : 'Show details'}
                  </button>
                </div>
                {orphanBannerOpen && (
                  <ul className="mt-2 space-y-1 text-xs" style={{ color: '#8A5A08' }}>
                    {orphans.slice(0, 5).map(o => (
                      <li key={`${o.roleId}-${o.module}`}>
                        <span className="font-semibold">{o.roleName}</span>{' '}
                        &rarr;{' '}
                        <span className="font-mono">{o.module}</span>{' '}
                        <span className="opacity-70">
                          ({o.actions.join(', ')})
                        </span>
                      </li>
                    ))}
                    {orphans.length > 5 && (
                      <li className="opacity-70">
                        &hellip;and {orphans.length - 5} more
                      </li>
                    )}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Main view */}
        {loading || rolesLoading ? (
          <div
            className="rounded-2xl p-6 animate-pulse space-y-3"
            style={{ background: SOVEREIGN.cardBg, border: `1px solid ${SOVEREIGN.cardBorder}` }}
          >
            <div className="h-8 rounded w-1/3" style={{ background: 'rgba(11,59,46,0.06)' }} />
            <div className="h-4 rounded w-2/3" style={{ background: 'rgba(11,59,46,0.04)' }} />
            <div className="h-48 rounded" style={{ background: 'rgba(11,59,46,0.03)' }} />
          </div>
        ) : features.length === 0 ? (
          <div
            className="rounded-2xl py-16 text-center"
            style={{ background: SOVEREIGN.cardBg, border: `1px solid ${SOVEREIGN.cardBorder}`, boxShadow: SOVEREIGN.cardShadow }}
          >
            <div className="text-5xl mb-3 opacity-30">&#128203;</div>
            <div className="text-sm font-semibold" style={{ color: SOVEREIGN.ink }}>
              No features yet
            </div>
            <div className="text-xs mt-1" style={{ color: SOVEREIGN.kpiCaption }}>
              {editable
                ? 'Click "+ Add Feature" to create your first one.'
                : "You don't have permission to create features."}
            </div>
          </div>
        ) : (
          <FeatureBUTabsView
            features={features}
            matrix={matrix}
            editable={editable}
            onEdit={setEditingFeature}
            onDelete={handleDelete}
            onToggleStatus={handleToggleStatus}
            onMakeCommon={handleMakeCommon}
            onAddSubMenu={(_parentKey, dept) => {
              setCreateDefaultDeptOverride(dept);
              setShowCreate(true);
            }}
            onMoveSubMenu={handleMoveSubMenu}
          />
        )}
      </div>

      {/* Create drawer */}
      <FeatureFormDrawer
        open={showCreate}
        mode="create"
        onClose={() => { setShowCreate(false); setCreateDefaultDeptOverride(null); }}
        onSuccess={handleCreateSuccess}
        defaultDepartment={createDefaultDeptOverride ?? 'COMMON'}
        existingKeys={existingKeys}
      />

      {/* Edit drawer */}
      <FeatureFormDrawer
        open={!!editingFeature}
        mode="edit"
        feature={editingFeature}
        onClose={() => setEditingFeature(null)}
        onSuccess={handleEditSaved}
      />
    </div>
  );
};

export default FeaturesMenuPage;
